import React, { useState } from 'react';
import { StorageService } from '../services/storage';
import { Check, Plus, X } from 'lucide-react';

interface AddToCollectionModalProps {
  appId: string;
  appName: string;
  isOpen: boolean;
  onClose: () => void;
}

export const AddToCollectionModal: React.FC<AddToCollectionModalProps> = ({
  appId,
  appName,
  isOpen,
  onClose
}) => {
  const [collections, setCollections] = useState(StorageService.getCollections());
  const [newCollectionName, setNewCollectionName] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleToggle = (colId: string) => {
    StorageService.addAppToCollection(colId, appId);
    setCollections(StorageService.getCollections());
    setToastMessage(`Added ${appName} to collection!`);
    setTimeout(() => setToastMessage(null), 2500);
  };

  const handleCreateNew = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCollectionName.trim()) return;
    const created = StorageService.createCollection(newCollectionName.trim(), 'Custom user app collection', [appId]);
    setCollections(StorageService.getCollections());
    setNewCollectionName('');
    setShowCreate(false);
    setToastMessage(`Created "${created.name}" and added ${appName}!`);
    setTimeout(() => setToastMessage(null), 2500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-slate-200 relative animate-scaleUp">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-slate-400 hover:text-slate-700 w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors"
        >
          <X className="w-4 h-4" strokeWidth={2} />
        </button>

        <div className="mb-4">
          <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Organize Apps</span>
          <h3 className="text-lg font-bold text-slate-900 mt-0.5">Add to Collection</h3>
          <p className="text-xs text-slate-500 mt-1">Select a collection for <strong className="text-slate-800">{appName}</strong></p>
        </div>

        {toastMessage && (
          <div className="mb-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold flex items-center gap-1.5 animate-fadeIn">
            <Check className="w-3.5 h-3.5 text-emerald-600" strokeWidth={2.5} />
            <span>{toastMessage}</span>
          </div>
        )}

        <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
          {collections.map((col) => {
            const hasApp = col.appIds.includes(appId);
            return (
              <div
                key={col.id}
                onClick={() => handleToggle(col.id)}
                className={`p-3 rounded-2xl border flex items-center justify-between cursor-pointer transition-all ${
                  hasApp
                    ? 'bg-blue-50/70 border-blue-200 text-blue-900'
                    : 'bg-slate-50 hover:bg-slate-100/70 border-slate-100 text-slate-800'
                }`}
              >
                <div>
                  <div className="font-bold text-sm flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.color }} />
                    {col.name}
                  </div>
                  <span className="text-[11px] text-slate-500">{col.appIds.length} apps saved</span>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-lg inline-flex items-center gap-1 ${hasApp ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-700'}`}>
                  {hasApp ? (
                    <>
                      <Check className="w-3 h-3" strokeWidth={2.5} />
                      <span>Saved</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-3 h-3" strokeWidth={2} />
                      <span>Add</span>
                    </>
                  )}
                </span>
              </div>
            );
          })}
        </div>

        {showCreate ? (
          <form onSubmit={handleCreateNew} className="p-3 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
            <input
              type="text"
              placeholder="Collection name (e.g. AI Startups, Top Competitors)..."
              value={newCollectionName}
              onChange={(e) => setNewCollectionName(e.target.value)}
              className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="text-xs text-slate-500 px-3 py-1.5 rounded-lg hover:bg-slate-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="text-xs bg-blue-600 text-white font-bold px-3.5 py-1.5 rounded-lg hover:bg-blue-700"
              >
                Create & Add
              </button>
            </div>
          </form>
        ) : (
          <button
            onClick={() => setShowCreate(true)}
            className="w-full py-2.5 border border-dashed border-slate-300 hover:border-blue-400 text-slate-600 hover:text-blue-600 rounded-2xl text-xs font-bold transition-colors inline-flex items-center justify-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2} />
            <span>Create New Collection</span>
          </button>
        )}
      </div>
    </div>
  );
};
